// Zustand B: aktives Coaching mit Cal.com-iframe + Sessions-Listen.

import { useState } from 'react';
import { CalendarClock, ExternalLink, Loader2 } from 'lucide-react';
import { BookingList } from './booking-list';
import { WeeklyCounter } from './weekly-counter';
import type { CoachingMeResponse } from '@/lib/coaching-api';

interface ActiveCoachingViewProps {
  data: CoachingMeResponse;
  calcomBookingUrl: string;
  onOpenPortal: () => Promise<void>;
}

const PLAN_LABEL: Record<string, string> = {
  quarterly: '3 Monate',
  half_yearly: '6 Monate',
  yearly: '12 Monate',
};

export function ActiveCoachingView({ data, calcomBookingUrl, onOpenPortal }: ActiveCoachingViewProps) {
  const [portalLoading, setPortalLoading] = useState(false);
  const sub = data.subscription!;
  const periodEnd = new Date(sub.period_end).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  return (
    <div className="space-y-3">
      <section className="glass-strong rounded-2xl border border-amber-500/20 p-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-[14px] font-bold text-amber-200">Dein Coaching</h2>
            <p className="text-[11px] text-neutral-400">
              Plan: <span className="text-neutral-200">{PLAN_LABEL[sub.plan] ?? sub.plan}</span> · Laeuft
              bis <span className="text-neutral-200 tabular">{periodEnd}</span>
              {sub.cancel_at_period_end ? ' · gekuendigt zum Ende' : ''}
              {' · Quelle '}
              <span className="text-neutral-200">
                {sub.source === 'stripe' ? 'Stripe' : 'manuell verwaltet'}
              </span>
            </p>
          </div>
          {sub.source === 'stripe' ? (
            <button
              type="button"
              onClick={() => {
                setPortalLoading(true);
                void onOpenPortal().finally(() => setPortalLoading(false));
              }}
              disabled={portalLoading}
              className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[11px] text-neutral-300 hover:bg-white/[0.08] disabled:opacity-40"
            >
              {portalLoading ? <Loader2 className="size-3 animate-spin" /> : <ExternalLink className="size-3" />}
              Stripe-Portal
            </button>
          ) : null}
        </div>
        <WeeklyCounter bookings={data.bookings_this_week} cap={3} />
      </section>

      <section className="glass-strong rounded-2xl p-4">
        <h3 className="mb-2 flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider text-amber-300">
          <CalendarClock className="size-3.5" />
          Termin buchen
        </h3>
        <div className="overflow-hidden rounded-xl border border-white/5">
          <iframe
            src={`${calcomBookingUrl}?embed=true`}
            title="Cal.com Booking"
            className="h-[720px] w-full bg-white"
            loading="lazy"
          />
        </div>
        <p className="mt-2 text-[10px] text-neutral-500">
          Cal.com regelt Verfuegbarkeit. Bestaetigung kommt per Mail.
        </p>
      </section>

      <div className="grid gap-3 md:grid-cols-2">
        <BookingList
          title="Naechste Sessions"
          bookings={data.upcoming_bookings}
          emptyText="Noch nichts gebucht — buche oben."
        />
        <BookingList
          title="Letzte Sessions"
          bookings={data.past_bookings}
          emptyText="Noch keine abgeschlossen."
        />
      </div>
    </div>
  );
}
